// Single source of truth for the demo question set. Imported by Demo.tsx
// (the demo runner with pass/fail checks), EmptyState.tsx (the suggestion
// grid users see on cold start), and AtlasShell.tsx's ⌘K palette.
//
// Questions are sourced from `HDW Demo Sample Questions.xlsx` — keep them
// faithful to that spreadsheet's intent. Edit there first, then mirror here.
//
// Coverage notes:
// - Q1, Q5, Q6, Q7 reference UDS / MIECHV data files HRSA publishes as
//   bulk Excel downloads (UDS Table 6a/6b/7/Appendix D, MIECHV performance
//   measures). Those aren't wired into the tool layer yet — the agent will
//   answer with what's in the ArcGIS / Grants / Bedrock-KB tools and
//   acknowledge the data gap. `partialDataCoverage: true` flags those so the
//   UI surfaces a chip and the demo runner uses lenient checks.
// - Q2, Q3, Q4 are fully answerable today via proximity / geo_enrich /
//   ArcGIS HPSA + Health Center Sites layers.
import type { Citation, Presentation } from './types';

export interface DemoResult {
  prose: string;
  presentation: Presentation;
  citations: Citation[];
  toolsUsed: string[];
  latencyMs: number;
  error?: string;
}

export type IconName =
  | 'info' | 'map' | 'table' | 'chart-bar' | 'chart-pie'
  | 'chart-line' | 'list' | 'poly';

export interface DemoQuestion {
  /** Q1..Q7 — must remain stable so report markdown can compare runs. */
  id: string;
  /** Short tile/card title — fits on one line. */
  shortLabel: string;
  /** The actual prompt sent to the agent. Address / health-center placeholders
   *  are filled with real worked examples so the demo is one-click runnable. */
  query: string;
  /** Dataset-chip labels shown under each card. Communicates "this question
   *  pulls from these sources" at a glance. */
  datasets: string[];
  /** Single-sentence "why this question matters" — verbatim Purpose column
   *  from the spreadsheet where present, paraphrased where blank. */
  purpose: string;
  iconName: IconName;
  /** True when one or more of the question's required datasets isn't wired
   *  into the tool layer yet. UI surfaces a "partial coverage" chip; demo
   *  runner falls back to a lenient check. */
  partialDataCoverage?: boolean;
  /** Pass criterion for the demo runner. Returns null on success, a short
   *  string explaining the failure otherwise. */
  check: (r: DemoResult) => string | null;
}

// ---- Helpers for the per-question check predicates ----
const usedTool = (r: DemoResult, kind: string) =>
  r.toolsUsed.some((t) => t.toUpperCase().includes(kind));
const proseHas = (r: DemoResult, ...needles: (string | RegExp)[]) =>
  needles.every((n) =>
    typeof n === 'string'
      ? r.prose.toLowerCase().includes(n.toLowerCase())
      : n.test(r.prose));
export const DEMO_QUESTIONS: DemoQuestion[] = [
  {
    id: 'Q1',
    shortLabel: 'Child healthcare investment over time',
    query:
      "How has HRSA's focus and investment in child healthcare through home " +
      "visits increased care for children over the years? Show the trend in " +
      "MCHB grants and the MIECHV program's reach.",
    datasets: ['MCHB Grants', 'MIECHV', 'UDS', 'HRSA Grants'],
    purpose: 'Show how the chatbot connects program inputs to outcomes.',
    iconName: 'chart-line',
    check: (r) => {
      // Strict now: hrsa_miechv covers home-visiting reach + benchmarks; the
      // agent should chain it with hrsa_grants for the MCHB-funding trend
      // portion. Accept either tool family — both feed the same answer.
      if (!(usedTool(r, 'MIECHV') || usedTool(r, 'GRANTS'))) {
        return 'expected MIECHV or GRANTS tool used';
      }
      const refsTopic = /(home visit|MIECHV|MCHB|child)/i.test(r.prose);
      if (!refsTopic) return 'expected home visits / MIECHV / children referenced';
      return null;
    },
  },
  {
    id: 'Q2',
    shortLabel: 'Personal: pediatric diabetes care, Medicaid',
    query:
      "I live at 1100 Bellaire Blvd, Houston, TX 77035 and I'm looking for a " +
      "doctor for my child who has diabetes. We're on Medicaid. Which HRSA-" +
      "funded health centers near me can see us?",
    datasets: ['Health Center Sites', 'Sites Detail', 'UDS', 'Geocoding'],
    purpose: 'Highlight personalization to a unique end-user need.',
    iconName: 'map',
    check: (r) => {
      // Geo step + the per-site detail filter for the personalization aspect.
      if (!(usedTool(r, 'PROXIMITY') || usedTool(r, 'GEO_ENRICH'))) {
        return 'expected PROXIMITY or GEO_ENRICH tool used';
      }
      // Strict now: hrsa_sites_detail filters by Medicaid + CHQR diabetes
      // badge — without it the agent cannot personalize beyond "here are
      // nearby health centers".
      if (!usedTool(r, 'SITES_DETAIL')) {
        return 'expected SITES_DETAIL tool used (Medicaid + diabetes badge filtering)';
      }
      if (!proseHas(r, /(health center|clinic|FQHC)/i)) {
        return 'expected health center / clinic referenced in prose';
      }
      if (!proseHas(r, /(medicaid|diabetes)/i)) {
        return 'expected Medicaid / diabetes referenced in prose';
      }
      return null;
    },
  },
  {
    id: 'Q3',
    shortLabel: 'Are my health-center sites in rural areas?',
    query:
      "I work at Erie Family Health Centers in Chicago. Can you help me " +
      "figure out which of our sites are located in rural areas?",
    datasets: ['Health Center Sites', 'FORHP Rural Areas'],
    purpose: 'Streamline grant-eligibility checks for HRSA analysts and grantees.',
    iconName: 'table',
    check: (r) => {
      // Sites discovered via ARCGIS or ORG_LOOKUP (the disambiguation tool
      // resolves the named org to a single grantee → sites query).
      if (!(usedTool(r, 'ARCGIS') || usedTool(r, 'ORG_LOOKUP'))) {
        return 'expected ARCGIS or ORG_LOOKUP tool used (find org + sites)';
      }
      // Strict now: hrsa_rural is the canonical FORHP rural-status table.
      if (!usedTool(r, 'RURAL')) {
        return 'expected RURAL tool used (FORHP canonical rural lookup)';
      }
      if (!proseHas(r, /rural/i)) return 'expected "rural" mentioned';
      const wantsTable = ['table', 'list', 'composite'].includes(r.presentation.type);
      if (!wantsTable) return `expected table/list/composite, got ${r.presentation.type}`;
      return null;
    },
  },
  {
    id: 'Q4',
    shortLabel: 'Primary Care HPSAs without Health Centers',
    query:
      "Are there any Primary Care HPSAs that don't have any Health Centers " +
      "within them? List those HPSAs with state, HPSA type (geographic vs " +
      "population), and whether each is in a rural area.",
    datasets: ['Primary Care HPSAs', 'Health Center Sites', 'FORHP Rural Areas'],
    purpose:
      'Demonstrate AI bringing intelligence to a "simple" cross-dataset task.',
    iconName: 'list',
    check: (r) => {
      // Strict now: HPSA detail (discipline + score + rural flag) +
      // ARCGIS or ORG_LOOKUP for the HC-sites side. RURAL is optional —
      // hrsa_hpsa_detail already carries a rural_status column from the
      // BCD_HPSA_FCT_DET_PC.xlsx file, so the cross-check can be done
      // without a second tool call.
      if (!usedTool(r, 'HPSA_DETAIL')) {
        return 'expected HPSA_DETAIL tool used (discipline + score + rural)';
      }
      if (!(usedTool(r, 'ARCGIS') || usedTool(r, 'ORG_LOOKUP'))) {
        return 'expected ARCGIS or ORG_LOOKUP for the Health Center sites side';
      }
      const refsAll = /hpsa/i.test(r.prose)
        && /(health center|hcp)/i.test(r.prose)
        && /rural/i.test(r.prose);
      if (!refsAll) return 'expected HPSA + health center + rural in prose';
      return null;
    },
  },
  {
    id: 'Q5',
    shortLabel: 'Rural+MUA pediatric screenings, 2024 vs 2023',
    query:
      "How many health centers in rural areas and Medically Underserved Areas " +
      "had visits related to screenings for patients under 18 in 2024 " +
      "compared to 2023?",
    datasets: ['UDS 2023', 'UDS 2024', 'MUA/P', 'FORHP Rural Areas'],
    purpose: 'Year-over-year cross-dataset trend across UDS + shortage designations.',
    iconName: 'chart-bar',
    check: (r) => {
      // Strict now that hrsa_uds ships: agent must use UDS year_compare for
      // the screenings-under-18 portion + ArcGIS (or geo_enrich) for the
      // rural/MUA overlap. Prose should mention both years.
      if (!usedTool(r, 'UDS')) return 'expected UDS tool used (year_compare 2023 vs 2024)';
      if (!proseHas(r, /(2023)/i, /(2024)/i)) {
        return 'expected both 2023 and 2024 referenced in prose';
      }
      return null;
    },
  },
  {
    id: 'Q6',
    shortLabel: 'Region with most food-insecurity in shortage areas',
    query:
      "Which HHS region has the highest percentage of health center patients " +
      "in shortage areas who experienced food insecurity?",
    datasets: ['UDS 2024 Appendix D', 'HPSAs', 'Health Center Sites'],
    purpose: 'Cross-dataset social-determinants-of-health analysis.',
    iconName: 'chart-pie',
    check: (r) => {
      // Strict now: hrsa_uds with table=food_insecurity, agg=by_region. The
      // agent should rank HHS regions and surface a winner.
      if (!usedTool(r, 'UDS')) return 'expected UDS tool used (food_insecurity by_region)';
      const refsRegion = /(region\s*\d|HHS region)/i.test(r.prose);
      if (!refsRegion) return 'expected HHS region referenced in prose';
      return null;
    },
  },
  {
    id: 'Q7',
    shortLabel: 'DMV vs national: chronic-disease screening in HPSAs',
    query:
      "How do health centers in the DMV (DC, MD, VA) compare to the national " +
      "average on chronic disease screenings and management in primary care " +
      "HPSAs?",
    datasets: ['UDS 2024 Tables 6b/7', 'Primary Care HPSAs'],
    purpose: 'Regional health-outcomes benchmark across UDS + HPSA designations.',
    iconName: 'chart-bar',
    check: (r) => {
      // Strict now: hrsa_uds with table=chronic_disease or clinical_quality,
      // filtered by state in {DC, MD, VA} vs national (no state filter).
      if (!usedTool(r, 'UDS')) return 'expected UDS tool used (chronic_disease/clinical_quality)';
      const refsRegion = /\b(DMV|DC|Washington|Maryland|MD|Virginia|VA)\b/i.test(r.prose);
      if (!refsRegion) return 'expected DMV / DC / MD / VA referenced in prose';
      return null;
    },
  },
];
